/**
 * The single "where are we?" derivation for an intervention (repair) — ONE
 * grammar shared by the detail spine, the detail "next action" block AND the
 * list mini-bar, so a repair can never look like "Devis" on the list and
 * "Intervention" on the detail.
 *
 * {@link computeInterventionPhase} is PURE, read-only, writes nothing and adds
 * NO Firestore field. It collapses the THREE state machines the screen used to
 * stack side by side — `Repair.status`, the work-request `stage`
 * ({@link deriveStage}) and the on-site visit lifecycle ({@link activeVisit}) —
 * into a single 0..4 phase index. It never re-implements those machines: it
 * reads their already-derived outputs and ranks them.
 *
 * Why a phase rather than three banners: the detail page deliberately dropped a
 * status stepper that duplicated the contractor card's own status line
 * (`repair-detail.component.ts`). A single ranked phase is what lets the spine
 * carry the "where" and the panels carry the "what" without re-stating status.
 *
 * Ranking (most-advanced wins): terminal > intervention > quote > provider >
 * reported. Two terminals are distinguished by {@link InterventionPhase.terminalKind}
 * (`closed` vs `canceled`) — a canceled repair sits at the terminal position but
 * must NOT render as a green completion (the caller reads `terminalKind`).
 *
 * Cases the visual prototype glossed over and this util handles explicitly:
 *  - manual assignment never produces a quote → `quoteSkipped` (phase 2 is shown
 *    muted, never "done");
 *  - `waiting_for_parts` is intervention-in-progress (phase 3), not a phase of
 *    its own;
 *  - `canceled` is terminal-but-not-closed;
 *  - the `'opened'` work-request stage is dead (never produced by
 *    `deriveStage`) → treated as "provider";
 *  - visits live in two homes (the shared ServiceRequest for a work request,
 *    `repair.visits` for a manual repair) — the caller resolves the right source
 *    and passes the already-computed `activeVisit`.
 */

import { Repair, RepairStatus, RepairVisit } from '../../../../../shared/definitions/repair';
import type { WorkRequestStage } from './repair-work-request.service';
import { activeVisit } from './repair-visit.util';
import {
  INTERVENTION_PHASES,
  type InterventionPhase,
  type InterventionPhaseIndex,
  type InterventionTerminalKind
} from '../../../../../shared/definitions/intervention-phase';

// The phase types + constants live in shared/definitions so the shared TaskCard
// can carry a precomputed phase without depending on this feature module — re-
// exported here so existing `./intervention-phase.util` imports keep working.
export * from '../../../../../shared/definitions/intervention-phase';

/**
 * Normalised inputs — already-derived outputs of the three state machines.
 * `stage` is null when there is no live work-request snapshot (the list view).
 */
export interface InterventionPhaseInputs {
  status: RepairStatus;
  /** Work-request stage, or null/undefined when unavailable (list view). */
  stage?: WorkRequestStage | null;
  /** A contractor is engaged (manual `assignedTo` OR a live work request). */
  hasContractor: boolean;
  /** The engagement is a work request (vs a manual free-text assignment). */
  isWorkRequest: boolean;
  /** Active visit resolved from the correct source, or null. */
  activeVisit: RepairVisit | null;
}

/**
 * Rank the three machines into a single phase. Pure, side-effect-free.
 * Most-advanced signal wins; terminals short-circuit everything.
 */
export function computeInterventionPhase(input: InterventionPhaseInputs): InterventionPhase {
  const { status, stage, hasContractor, isWorkRequest, activeVisit: visit } = input;
  // A manual repair never goes through a quote round — flag it so the spine can
  // mute (never "complete") the Devis position.
  const quoteSkipped = hasContractor && !isWorkRequest;

  const at = (
    index: InterventionPhaseIndex,
    terminalKind: InterventionTerminalKind | null = null,
  ): InterventionPhase => ({ index, key: INTERVENTION_PHASES[index], terminalKind, quoteSkipped });

  // Terminal first — an aborted/closed repair outranks any in-flight signal.
  if (status === RepairStatus.Canceled || stage === 'cancelled') return at(4, 'canceled');
  if (
    status === RepairStatus.Completed ||
    status === RepairStatus.Resolved ||
    stage === 'completed'
  ) {
    return at(4, 'closed');
  }

  // Intervention underway — a live visit, or work explicitly accepted/started.
  if (visit) return at(3);
  if (status === RepairStatus.InProgress || status === RepairStatus.WaitingForParts) return at(3);
  if (stage === 'accepted' || stage === 'in_progress') return at(3);

  // Quote received, awaiting validation (work request only).
  if (stage === 'quoted') return at(2);

  // Provider engaged — manual assignment, or a request already sent.
  // (`'opened'` is dead in `deriveStage`; it lands here via `hasContractor`.)
  if (hasContractor || stage === 'sent') return at(1);
  if (status === RepairStatus.Assigned) return at(1);

  // Nothing engaged yet.
  return at(0);
}

/**
 * Phase from a bare {@link Repair} — the LIST view, which has no live
 * work-request snapshot. Lower fidelity by design: without the `stage` it cannot
 * tell "provider" from "quote received", and a work-request repair keeps its
 * visits on the ServiceRequest (not on `repair.visits`), so a planned
 * appointment may read as phase 1 instead of 3. The denormalised `repair.status`
 * (`in_progress`/`completed`/`canceled`) still carries the coarse truth. Accept
 * this limit, or denormalise the stage onto the repair (backend) — out of scope.
 */
export function interventionPhaseFromRepair(repair: Repair): InterventionPhase {
  const isWorkRequest = !!repair.interventionUID;
  return computeInterventionPhase({
    status: repair.status,
    stage: null,
    hasContractor: !!repair.assignedTo || isWorkRequest,
    isWorkRequest,
    activeVisit: activeVisit(repair.visits),
  });
}

/**
 * Phase from the DETAIL view's live signals. `mode` is the contractor panel's
 * own `'empty' | 'manual' | 'workRequest'`; the `stage` is only trusted for a
 * work request (the empty snapshot reports a misleading `'sent'`). The caller
 * passes the visit resolved from the right home
 * (`activeServiceRequest.visits ?? repair.visits`).
 */
export function interventionPhaseFromWorkRequest(args: {
  status: RepairStatus;
  stage: WorkRequestStage;
  mode: 'empty' | 'manual' | 'workRequest';
  activeVisit: RepairVisit | null;
}): InterventionPhase {
  return computeInterventionPhase({
    status: args.status,
    stage: args.mode === 'workRequest' ? args.stage : null,
    hasContractor: args.mode !== 'empty',
    isWorkRequest: args.mode === 'workRequest',
    activeVisit: args.activeVisit,
  });
}
